import { createContext, useContext, useState } from "react";
import usePersistedState from "../hooks/usePersistedState";
import Client from "../utils/client";
const UserInfoContext = createContext();

const UserInfoProvider = ({ children }) => {
  const c = new Client();

  const [emailAddress, setEmailAddress] = usePersistedState("emailAddress", "");
  const [firstName, setFirstName] = usePersistedState("firstName", "");
  const [lastName, setLastName] = usePersistedState("lastName", "");
  const [postcode, setPostcode] = usePersistedState("postcode", "");
  const [address, setAddress] = usePersistedState("address", "");
  const [addressId, setAddressId] = usePersistedState("addressId", "");
  const [addressResults, setAddressResults] = useState([]);
  const [fetchingAddressResults, setFetchingAddressResults] = useState(false);
  const [isEditingPostcode, setIsEditingPostcode] = useState(true);
  const [postcodeError, setPostcodeError] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState(false);
  const [showAddressPageError, setShowAddressPageError] = useState(false);
  const [gasOnlyAddressError, setGasOnlyAddressError] = useState(false);
  const [showAddressEmptyWarning, setShowAddressEmptyWarning] = useState(false);
  const [hasSeenPriceCapInfoModal, setHasSeenPriceCapInfoModal] =
    usePersistedState("hasSeenPriceCapInfoModal", false);

  const resetAddressStates = () => {
    setPostcodeError(false);
    setAddressLookupError(false);
    setIsEditingPostcode(true);
    setFetchingAddressResults(false);
    setShowAddressEmptyWarning(false);
    setGasOnlyAddressError(false);
  };

  const lookupAddress = async (pc) => {
    setPostcodeError(false);
    setAddressLookupError(false);
    setFetchingAddressResults(true);
    setShowAddressEmptyWarning(false);

    if (pc !== postcode) {
      setAddress("");
    }

    try {
      const results = await c.lookupAddress(pc);
      setAddressResults(results);
      setIsEditingPostcode(false);
    } catch (error) {
      setAddressResults([]);
      setPostcodeError(true);
      setAddressLookupError(true);
      setIsEditingPostcode(true);
      console.log(error);
    }

    setFetchingAddressResults(false);
  };

  const createSession = async (email, firstName, lastName) =>
    await c.createSession(email, firstName, lastName);

  return (
    <UserInfoContext.Provider
      value={{
        firstName,
        lastName,
        setFirstName,
        setLastName,
        emailAddress,
        setEmailAddress,
        createSession,
        postcode,
        setPostcode,
        address,
        setAddress,
        addressId,
        setAddressId,
        addressResults,
        fetchingAddressResults,
        isEditingPostcode,
        setIsEditingPostcode,
        lookupAddress,
        postcodeError,
        setPostcodeError,
        addressLookupError,
        resetAddressStates,
        hasSeenPriceCapInfoModal,
        setHasSeenPriceCapInfoModal,
        showAddressPageError,
        setShowAddressPageError,
        showAddressEmptyWarning,
        setShowAddressEmptyWarning,
        gasOnlyAddressError,
        setGasOnlyAddressError,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

const useUserInfo = () => {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error("useUserInfo must be used within a UserInfoProvider");
  }
  return context;
};

export { UserInfoContext, UserInfoProvider, useUserInfo };
